import { isEmptyValue } from './bookingGate';
import type {
  BookEventBranch,
  BookEventPayload,
  BookEventPortalConfig,
  BookEventUser,
} from './bookEvent.types';
import { EVENT_TYPES } from './bookEventServiceRules';

const BOOKABLE_PAYMENT_TYPES = new Set(['card', 'pay_later']);

function isBookablePaymentMethod(
  method: NonNullable<BookEventBranch['paymentMethods']>[number]
): boolean {
  return [
    method.active === true,
    method.staff_only !== true,
    BOOKABLE_PAYMENT_TYPES.has((method.type_id ?? '').toLowerCase()),
  ].every(Boolean);
}

function hasBookablePaymentMethod(branch: BookEventBranch | null): boolean {
  return (branch?.paymentMethods ?? []).some(isBookablePaymentMethod);
}

function isDefinedNumber(value: number | undefined): value is number {
  return value != null;
}

export function isGoingToWaitingList(
  event: BookEventPayload,
  branch: BookEventBranch | null
): boolean {
  const booking = branch?.features?.booking;
  const counts = [
    event.booked,
    event.size,
    event.waiting,
    booking?.waiting_list,
  ];
  if (!counts.every(isDefinedNumber)) {
    return false;
  }
  const [booked, size, waiting, waitingListSize] = counts;
  return [
    event.type === EVENT_TYPES.CLASS,
    booked >= size,
    Boolean(booking?.waiting_list_enabled),
    waiting < waitingListSize,
  ].every(Boolean);
}

export function isAllowToBook(
  event: BookEventPayload,
  branch: BookEventBranch | null,
  priceToPay: number | null,
  portalConfig: BookEventPortalConfig
): boolean {
  if (event.type === EVENT_TYPES.CLASS && !portalConfig.isClassBookingEnabled) {
    return false;
  }
  return isAppointmentAllowToBook(branch, priceToPay);
}

// Appointments never consulted the class-booking portal flag.
export function isAppointmentAllowToBook(
  branch: BookEventBranch | null,
  priceToPay: number | null
): boolean {
  if ((priceToPay ?? 0) <= 0) {
    return true;
  }
  return hasBookablePaymentMethod(branch);
}

export function isUserFirstBook(user: BookEventUser | null): boolean {
  return !isEmptyValue(user) && (user?.PAYGPAYMENT === undefined || user?.PAYGPAYMENT === false);
}

export function isUserLoggedIn(user: BookEventUser | null): boolean {
  return !isEmptyValue(user);
}

export function gotOutOfCredits(guests: number, creditsToPay: number): boolean {
  return guests + 1 > creditsToPay;
}

export function hasSpotAvailabilityPrerequisites(args: {
  event: BookEventPayload;
  flags: { isInteractiveSpotBookingEnabled: boolean; isInteractiveSpotBookingPhase2Enabled: boolean };
  userToken: string | null;
}): boolean {
  return (
    args.flags.isInteractiveSpotBookingEnabled &&
    args.flags.isInteractiveSpotBookingPhase2Enabled &&
    (args.event.type === EVENT_TYPES.CLASS || args.event.type === EVENT_TYPES.EVENT) &&
    !!args.event.layout_id &&
    !!args.userToken
  );
}

export function shouldUseInteractiveSpotBooking(args: {
  event: BookEventPayload;
  branch: BookEventBranch | null;
  isInteractiveSpotBookingEnabled: boolean;
  isSpotBookingEnabledForEvent: boolean;
}): boolean {
  const isClassEvent =
    args.event.type === EVENT_TYPES.CLASS || args.event.type === EVENT_TYPES.EVENT;
  return (
    args.isInteractiveSpotBookingEnabled &&
    isClassEvent &&
    !isGoingToWaitingList(args.event, args.branch) &&
    (args.event.booked || 0) < (args.event.size || 0) &&
    args.isSpotBookingEnabledForEvent
  );
}
